import React from 'react';
import LegalLayout from '../components/LegalLayout';
import { 
  HiOutlineXCircle, 
  HiOutlineClock, 
  HiOutlineCalendar, 
  HiOutlineCheck,
  HiOutlineExclamationCircle
} from 'react-icons/hi2';

const sections = [
  { id: 'overview', title: '1. Policy Overview' },
  { id: 'on-demand', title: '2. On-Demand Orders' },
  { id: 'subscriptions', title: '3. Subscription Plans' },
  { id: 'corporate-orders', title: '4. Corporate & Event Orders' },
  { id: 'how-to-cancel', title: '5. Steps to Cancel' },
];

const CancellationPolicy = () => {
  return (
    <LegalLayout
      title="Cancellation Policy"
      subtitle="Clear guidelines on cancelling on-demand fruit orders, corporate catering, and daily subscription plans."
      lastUpdated="October 09, 2026"
      sections={sections}
    >
      <div className="space-y-8 text-gray-700">
        
        {/* Intro Banner */}
        <div className="p-4 rounded-lg bg-orange-50/60 border border-orange-200/60 flex items-start gap-3">
          <HiOutlineXCircle className="text-xl text-[#FF410B] shrink-0 mt-0.5" />
          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed font-normal">
            At <span className="font-semibold text-gray-900">Falhari</span>, we prepare fresh handcrafted fruit meals upon order. Because cut fruits have a limited fresh shelf life, order cancellations are governed by preparation cutoffs outlined below.
          </p>
        </div>

        {/* Section 1 */}
        <section id="overview" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">1. Cancellation Guidelines Overview</h2>
          <p className="text-sm leading-relaxed text-gray-700">
            We understand plans change. You can cancel your order or pause your fruit subscription plan easily, provided the request is made before fruit cutting and kitchen preparation begin.
          </p>
        </section>

        {/* Section 2 - On-demand */}
        <section id="on-demand" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">2. On-Demand & Same-Day Orders</h2>
          <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="font-semibold text-gray-900 text-xs flex items-center gap-1.5">
                <HiOutlineClock className="text-[#FF410B] text-base" />
                Cancellation Window: Within 5 Minutes
              </span>
              <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[11px] font-semibold">
                Zero Cancellation Fee
              </span>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              You can cancel an order free of charge within <span className="font-semibold text-gray-800">5 minutes</span> of placement or before kitchen preparation starts.
            </p>
            <div className="pt-2 border-t border-slate-200 text-xs text-amber-800 font-normal flex items-center gap-1.5">
              <HiOutlineExclamationCircle className="text-sm text-amber-600 shrink-0" />
              <span>Once preparation has commenced or the delivery executive has been assigned, orders cannot be cancelled or refunded.</span>
            </div>
          </div>
        </section>

        {/* Section 3 - Subscriptions */}
        <section id="subscriptions" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">3. Fruit Subscription Plans (Daily / Weekly)</h2>
          <p className="text-sm text-gray-700 leading-relaxed mb-2">For recurring fruit salad & juice subscription packages:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            
            <div className="p-3.5 rounded-lg bg-white border border-gray-200 space-y-1">
              <span className="font-semibold text-gray-900 text-xs flex items-center gap-1.5">
                <HiOutlineCalendar className="text-[#FF410B] text-sm" />
                Morning Slot Cutoff
              </span>
              <p className="text-xs text-gray-600 leading-relaxed">
                To pause or cancel next morning's delivery, notify us by <span className="font-semibold text-gray-800">8:00 PM</span> the previous evening.
              </p>
            </div>

            <div className="p-3.5 rounded-lg bg-white border border-gray-200 space-y-1">
              <span className="font-semibold text-gray-900 text-xs flex items-center gap-1.5">
                <HiOutlineCalendar className="text-[#FF410B] text-sm" />
                Evening Slot Cutoff
              </span>
              <p className="text-xs text-gray-600 leading-relaxed">
                To pause or cancel evening delivery, notify us by <span className="font-semibold text-gray-800">12:00 PM</span> noon on the same day.
              </p>
            </div>

          </div>
          <p className="text-xs text-gray-500 pt-1">
            * Unused subscription balance will remain credited in your account wallet or pro-rated upon plan cancellation.
          </p>
        </section>

        {/* Section 4 - Corporate */}
        <section id="corporate-orders" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">4. Corporate Catering & Bulk Orders</h2>
          <p className="text-sm text-gray-700 leading-relaxed mb-2">For corporate subscriptions, event catering, and bulk orders:</p>
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
            <li>Cancellations made <span className="font-semibold text-gray-800">24 hours prior</span> to event delivery slot receive 100% refund.</li>
            <li>Cancellations within 12 to 24 hours incur a 30% procurement fee due to advance fresh fruit sourcing.</li>
            <li>Cancellations under 12 hours cannot be refunded.</li>
          </ul>
        </section>

        {/* Section 5 - Steps */}
        <section id="how-to-cancel" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">5. How to Request Cancellation</h2>
          <p className="text-sm text-gray-700 leading-relaxed mb-2">You can cancel or pause your order through:</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
              <HiOutlineCheck className="text-[#FF410B] font-bold text-sm shrink-0" />
              <span className="text-xs font-normal text-gray-800">Online ordering portal / App account management section</span>
            </div>
            <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
              <HiOutlineCheck className="text-[#FF410B] font-bold text-sm shrink-0" />
              <span className="text-xs font-normal text-gray-800">Direct WhatsApp support chat</span>
            </div>
            <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
              <HiOutlineCheck className="text-[#FF410B] font-bold text-sm shrink-0" />
              <span className="text-xs font-normal text-gray-800">Customer Helpline: +91 9810387325 or hello@falhari.com</span>
            </div>
          </div>
        </section>

      </div>
    </LegalLayout>
  );
};

export default CancellationPolicy;
